import { Controller } from "@hotwired/stimulus"
import FamilyTree2 from "../familytree2"

// Names come from the database: escape them before putting them into HTML.
const escapeHtml = (text) =>
  String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

// Connects to data-controller="family-tree"
export default class extends Controller {
  static targets = ["tree", "dialog"]
  static values = {
    people: Array,
    focus: Number
  }

  disconnect() {
    if (this.familyTree) {
      this.familyTree.destroy();
      this.familyTree = null;
    }
  }

  connect() {
    if (this.familyTree) return;

    console.log("FamilyTreeController connected");

    const cookieName = 'how_to_guide_seen';
    const cookieExists = document.cookie.split(';').some(c => c.trim().startsWith(cookieName + '='));
    if (!cookieExists) {
      document.getElementById('tree-info-dialog').showModal();
      const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${cookieName}=1; expires=${expires}; path=/`;
    }
    
    this.ensureFamilyTree2().then(() => {

        const colorLightblue = getComputedStyle(document.documentElement).getPropertyValue('--color-lightblue-hex').trim();
        const colorRed = getComputedStyle(document.documentElement).getPropertyValue('--color-red-hex').trim();
        const colorPurple = getComputedStyle(document.documentElement).getPropertyValue('--color-purple-hex').trim();
        const colorWhite = getComputedStyle(document.documentElement).getPropertyValue('--color-white-hex').trim();
        const colorBlack = getComputedStyle(document.documentElement).getPropertyValue('--color-black-hex').trim();

        let FHFTemplate = FamilyTree2.createTemplate('FHFTemplate');

        FHFTemplate.layout = 'layout3';
        FHFTemplate.link.stroke = colorLightblue;
        FHFTemplate.link.strokeWidth = 5;
        FHFTemplate.width = 220;
        FHFTemplate.height = 150;
        FHFTemplate.nodes.focus.width = 220;
        FHFTemplate.nodes.focus.height = 180;

        FHFTemplate.html = function(node){ return `` };

        // Define basic node shape
        FHFTemplate.svg = function (node) {
            const width = node.width;
            const height = node.height;

            // Loading animation
            let loading = `
                <g transform="translate(${node.width / 2 - 24} ${node.height / 2 - 24})">
                    <circle fill="${colorPurple}" cx="36" cy="24" r="0" >
                        <animate attributeName="r" begin=".67" calcMode="spline" dur="1.5s" keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8" repeatCount="indefinite" values="0;8;0;0"/>
                    </circle>
                    <circle fill="${colorPurple}" cx="24" cy="24" r="0" >
                        <animate attributeName="r" begin=".33" calcMode="spline" dur="1.5s" keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8" repeatCount="indefinite" values="0;8;0;0"/>
                    </circle>
                    <circle fill="${colorPurple}" cx="12" cy="24" r="0" >
                        <animate attributeName="r" begin="0" calcMode="spline" dur="1.5s" keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8" repeatCount="indefinite" values="0;8;0;0"/>
                    </circle>
                </g>
            `;

            // Default node shape — use tint colour as background if set
            const bgColor = node.familyMember.tint || colorWhite;
            let result = `
                <rect x="0" y="0" width="${width}" height="${height}" fill="${bgColor}" stroke="${colorLightblue}" stroke-width="5" rx="10" />
            `;

            // Add loading animation if the node is loading
            if (node.familyMember && node.familyMember.loading) {
                result += `${loading}`;
            }

            return result;
        };

        // Add extra parts to the nodes
        FHFTemplate.insertSvg = function (node) {
            if (!(node.familyMember.loading)) {
                if (node.familyMember.obfuscated) {
                    return `
                        <foreignObject x="10" y="0" width="${node.width - 20}" height="${node.height}" class="is-obfuscated">
                            <div xmlns="http://www.w3.org/1999/xhtml" 
                                style="width:100%; height:100%; color:${colorBlack}; display:flex; flex-direction: column; gap: 0.5rem; align-items:center; justify-content:center; text-align:center; font-size:16px; line-height:1.2; filter: blur(5px);">
                                <div class="full_name">[ OBFUSCATED ]</div>
                            </div>
                        </foreignObject>
                    `;
                } else {
                    let lifetime = ``;
                    if (node.familyMember.lifetime) {
                        lifetime = `<div>${node.familyMember.lifetime}</div>`;
                    }

                    let biography = ``;
                    if (node.familyMember.biography) {
                        biography = `<span style="display:inline-block; width:1.5rem; height:1.5rem; background:url('/info-purple.svg') no-repeat center center; background-size:contain; flex-shrink:0;"></span>`;
                    }

                    const lifetimeAreaStyle = `display:flex; flex-direction:row; align-items:center; justify-content:center; gap:0.5rem;`;

                    if (node.familyMember.photo) {
                        return `
                            <clipPath id="photo-clip">
                                <rect stroke-width="0" stroke="#fff" x="${node.width / 2}" y="15" rx="50" ry="50" width="70" height="70"></rect>
                            </clipPath>
                            <image preserveAspectRatio="xMidYMid slice"

                                xlink:href="${node.familyMember.photo}"
                                x="${node.width / 2 - 25}" y="10" width="50" height="50">
                            </image>

                            <foreignObject x="10" y="60" width="${node.width - 20}" height="${node.height - 60}">
                                <div xmlns="http://www.w3.org/1999/xhtml"
                                    style="width:100%; height:100%; color:${colorBlack}; display:flex; flex-direction: column; gap: 0.5rem; align-items:center; justify-content:center; text-align:center; font-size:16px; line-height:1.2;">
                                    <div class="full_name">${escapeHtml(node.familyMember.name)}</div>
                                    <div style="${lifetimeAreaStyle}">${lifetime}${biography}</div>
                                </div>
                            </foreignObject>
                        `;
                    } else {
                        return `
                            <foreignObject x="10" y="0" width="${node.width - 20}" height="${node.height}">
                                <div xmlns="http://www.w3.org/1999/xhtml"
                                    style="width:100%; height:100%; color:${colorBlack}; display:flex; flex-direction: column; gap: 0.5rem; align-items:center; justify-content:center; text-align:center; font-size:16px; line-height:1.2;">
                                    <div class="full_name">${escapeHtml(node.familyMember.name)}</div>
                                    <div style="${lifetimeAreaStyle}">${lifetime}${biography}</div>
                                </div>
                            </foreignObject>
                        `;
                    }
                }
            }
        }

        // FHFTemplate.insertSvg = function (node) {
        //     if (node.familyMember.lifetime) {
        //         return `
        //             <text fill="white" text-anchor="middle"
        //                 x="${node.width / 2}" y="${node.height / 2 + 45}">
        //                 ${node.familyMember.lifetime}
        //             </text>
        //         `;
        //     }
        // }



        let tree = this.treeTarget;
        let people = this.peopleValue;
        let focus = this.focusValue;

        this.familyTree = new FamilyTree2(tree);
        let familyTree = this.familyTree;

        familyTree.readOnly = true;
        familyTree.templateName = 'FHFTemplate';

        let globalDistance = 80;
        let separations = familyTree.template.separations;

        separations.focusSide = globalDistance;
        separations.parentLevel = globalDistance;
        separations.parentSide = globalDistance;
        separations.parentNeighbor = globalDistance;
        separations.childInLowNeighbor = globalDistance;
        separations.focusBottom = globalDistance;
        separations.childSide = globalDistance;
        separations.childLevel = globalDistance;
        separations.childNeighbor = globalDistance;
        separations.parentSiblingNeighbor = globalDistance;
        separations.parentSiblingLevel = globalDistance;
        separations.parentInLawLevel = globalDistance;
        separations.parentInLawNeighbor = globalDistance;
        separations.piblingNeighbor = globalDistance;
        separations.siblingNeighbor = globalDistance;
        separations.spouseNeighbor = globalDistance;
        separations.stepParentNeighbor = globalDistance;

        familyTree.addFamilyMembers(people).draw(focus || null);

    //     familyTree.addFamilyMembers([
    //     { 
    //         id: 1, 
    //         fatherId: 2, 
    //         motherId: 3,  
    //         spouseIds: [4], 
    //         siblingIds: [5, 6], 
    //         childIds: [7],
    //         name: 'Me' 
    //     }, 
    //     {id: 2, name: 'Father'},
    //     {id: 3, name: 'Mother'},
    //     {id: 4, childIds: [7], name: 'Spouse'},
    //     {id: 5, name: 'Sibling 1'},
    //     {id: 6, name: 'Sibling 2'},
    //     {id: 7, name: 'Child'}
    // ]).draw(1);

        familyTree.controlsUI.show({
            zoom_in: {title: 'Zoom in', anchor: FamilyTree2.anchor.bottom_right},
            zoom_out: {title: 'Zoom out', anchor: FamilyTree2.anchor.bottom_right},
            fit: {title: 'Fit', anchor: FamilyTree2.anchor.bottom_right},
        });

        familyTree.onDemand(function (args) {
            for (var id of args.ids) {                    
                this.addFamilyMembers([{ id: id, loading: true }]);
            }
            this.draw(this.focusId, null, function(){
                // this.fit();
                fetch(`/fetch_individuals/${args.ids.join(',')}`)
                    .then((data) => data.json())
                    .then((data) => familyTree.addFamilyMembers(data).draw())
            });
        });
        
        familyTree.onNodeClick(function(args) {
            if (this.readOnly){
                familyTree.center(args.node);
            }

            if (args.node.familyMember.obfuscated !== true) {
                let id = parseInt(args.node.id);
                let node_detail_dialog = document.querySelector('#node_detail_dialog');

                fetch("/fetch_node_detail/" + id)
                .then(function (response) {
                    return response.text();
                })
                .then(function (data) {
                    var parser = new DOMParser();
                    var html = parser.parseFromString(data, "text/html");

                    node_detail_dialog.innerHTML = new XMLSerializer().serializeToString(html);
                    node_detail_dialog.showModal();

                    // Focuses on the node when clicked
                    document.querySelector('.focus-button').addEventListener("click", async function (e) {
                        id = this.dataset.id;
                        node_detail_dialog.close();
                        
                        console.log(familyTree.getFamilyMember(id));

                        // Collapses siblings
                        familyTree.collapsedIds = familyTree.getFamilyMember(id).siblingIds;

                        // Expands children
                        var response = await fetch(`/fetch_individuals/${familyTree.getFamilyMember(id).childIds.join(',')}`);
                        var data = await response.json();
                        familyTree.addFamilyMembers(data);

                        familyTree.draw(id);

                        updateFocusDescription(familyTree.getFamilyMember(id));
                    });
                })
                .catch(function (err) {
                    console.warn("Something went wrong.", err);
                });
            }
        });

        function updateFocusDescription(member) {
            if (!member) return;
            const strongClass = member.obfuscated ? ' class="is-obfuscated"' : '';
            const memberName = member.obfuscated ? '[ OBFUSCATED ]' : escapeHtml(member.name);
            const descEl = document.querySelector('.tree-description');
            if (descEl) descEl.innerHTML = `Tree focusing on <strong${strongClass}>${memberName}</strong>`;
        }

        familyTree.onFocusButtonClick(function(args) {
            console.log("Focus button clicked");
            updateFocusDescription(familyTree.getFamilyMember(args.newFocusId));
        });

        console.log(people);
    }).catch((error) => {
      console.error("Failed to load FamilyTree2", error);
    });
  }

  ensureFamilyTree2() {
    return new Promise((resolve, reject) => {
      if (typeof FamilyTree2 !== "undefined") {
        resolve();
      } else {
        // Wait and check periodically
        const interval = setInterval(() => {
          if (typeof FamilyTree2 !== "undefined") {
            clearInterval(interval);
            resolve();
          }
        }, 50);

        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(interval);
          reject("FamilyTree2 did not load in time");
        }, 5000);
      }
    });
  }
}
